import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import { PageHeader } from '@/components/PageHeader';

/**
 * Emergency & crisis support page.
 *
 * Displays jurisdiction-aware emergency contact information. The list is
 * intentionally hard-coded for Tour 2 — the data is small, doesn't change,
 * and we want it available even if the API is down. Tour 5 (RegulationOps
 * console) wires the same data to the `MedicalJurisdiction.emergency_escalation_copy`
 * field for editorial control.
 *
 * The layout is deliberately bold and quiet: anyone landing here is in
 * distress. No marketing, no upsell, no chrome.
 */

const EMERGENCY_NUMBERS = [
  { country: 'European Union', number: '112', tagline: 'general emergency' },
  { country: 'United States',  number: '911', tagline: 'general emergency' },
  { country: 'United Kingdom', number: '999', tagline: 'general emergency' },
];

const CRISIS_LINES = [
  {
    region: 'United States',
    name: '988 Suicide & Crisis Lifeline',
    number: '988',
    note: 'Call or text. Free, confidential, 24/7.',
  },
  {
    region: 'United Kingdom',
    name: 'Samaritans',
    number: '116 123',
    note: 'Free from any phone, 24/7.',
  },
  {
    region: 'Poland',
    name: 'Telefon Zaufania dla Dzieci i Młodzieży',
    number: '116 111',
    note: 'Bezpłatny, dla osób młodych.',
  },
  {
    region: 'Poland',
    name: 'Centrum Wsparcia dla Osób w Stanie Kryzysu Psychicznego',
    number: '800 70 2222',
    note: '24h, bezpłatny.',
  },
];

export function PatientSafetyResourcesPage() {
  return (
    <>
      <PageHeader title="Emergency & crisis support" />

      <Alert severity="error" icon={false} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Life-threatening emergency?
        </Typography>
        <Typography variant="body2">
          Call your local emergency number now. This app is not for emergencies.
        </Typography>
      </Alert>

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LocalPhoneIcon color="error" />
              <Typography variant="h6">Emergency numbers</Typography>
            </Stack>
            <Stack spacing={1.5}>
              {EMERGENCY_NUMBERS.map((e) => (
                <Stack
                  key={e.country}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={500}>{e.country}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.tagline}
                    </Typography>
                  </Box>
                  <Button
                    href={`tel:${e.number}`}
                    variant="contained"
                    color="error"
                    startIcon={<LocalPhoneIcon />}
                    sx={{ minWidth: 120 }}
                  >
                    {e.number}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <HealthAndSafetyIcon color="primary" />
              <Typography variant="h6">Mental-health crisis lines</Typography>
            </Stack>
            <Stack spacing={2}>
              {CRISIS_LINES.map((c, i) => (
                <Box key={i}>
                  <Typography variant="overline" color="text.secondary"
                              sx={{ display: 'block' }}>
                    {c.region}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center"
                         sx={{ mt: 0.5 }}>
                    <Box sx={{ minWidth: 0, mr: 2 }}>
                      <Typography variant="body1" fontWeight={500}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.note}
                      </Typography>
                    </Box>
                    <Button
                      href={`tel:${c.number.replace(/\s/g, '')}`}
                      variant="outlined"
                      startIcon={<LocalPhoneIcon />}
                    >
                      {c.number}
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          MyBooking is not a healthcare provider and cannot offer emergency or
          crisis services. If you are in immediate danger, please use the resources above.
        </Typography>
      </Stack>
    </>
  );
}
